import { ColumnsType } from "antd/es/table";
import {
  DocumentsTableDataResponseType,
  getColumnsParamsType,
} from "../../../Types/DatabaseTabTypes";
import pdfIcon from "../../../assets/images/view_pdf.svg";
import jpgIcon from "../../../assets/images/jpeg-image-icon.svg";
import pngIcon from "../../../assets/images/png-image-icon.svg";
import excelIcon from "../../../assets/images/excel-file-icon.svg";
import csvIcon from "../../../assets/images/csvIcon.svg";
import docIcon from "../../../assets/images/docIcon.svg";
import fileIcon from "../../../assets/images/default_file.svg";
import clsx from "clsx";
import styles from "./DocumentsTable.module.scss";
import { MdDelete, MdModeEdit } from "react-icons/md";
import { EllipsisOutlined } from "@ant-design/icons";
import { message, Popover } from "antd";
import { PlacementPositions } from "../../../enums/PlacementPositionsEnums";
import * as Yup from "yup";
import { DatabaseSections, DocumentTypes } from "../../../enums/DatabaseEnums";
import DescriptionCell from "../../../reusable-components/DescriptionCell";
import { ArchiveId, ArchiveKeys } from "../../../enums/ArchiveActionKeys";
import { downloadUtil } from "../../../utils/downloadUtil";
import ConditionalRender from "../../../reusable-components/ConditionalRender";

const iconMapping = {
  "": fileIcon,
  pdf: pdfIcon,
  csv: csvIcon,
  xls: excelIcon,
  xlsx: excelIcon,
  doc: docIcon,
  docx: docIcon,
  png: pngIcon,
  jpeg: jpgIcon,
  jpg: jpgIcon,
};

export const getColumns = (
  params?: getColumnsParamsType,
): ColumnsType<DocumentsTableDataResponseType> => [
  params?.isQuarry
    ? {
        title: "MATERIAL",
        key: "type",
        align: "center",
        dataIndex: "materialName",
        render: (val) => <span className="table-font-mobile">{val}</span>,
      }
    : {
        title: "TYPE",
        key: "type",
        align: "center",
        dataIndex: "docType",
        render: (val) => <span className="table-font-mobile">{val}</span>,
      },
  {
    title: "DOCUMENT NAME",
    key: "documentName",
    align: "center",
    dataIndex: "docName",
    render: (val, record) => {
      const fileType = record?.docAttachment?.split(".")?.slice(-1)?.[0] ?? "";
      const documentName = String(val ?? "");
      const hasFileExtension =
        !!fileType &&
        documentName.toLowerCase().endsWith(`.${fileType.toLowerCase()}`);
      return (
        <span
          onClick={async () => {
            message.loading("Downloading");
            await downloadUtil(record?.docAttachment)?.then(() =>
              message.destroy(),
            );
          }}
          className={clsx("table-font-mobile", styles.documentColor)}
        >
          <img
            src={iconMapping[fileType]}
            width={"20px"}
            alt={`${fileType} icon`}
          />{" "}
          {documentName}
          {!hasFileExtension && fileType ? `.${fileType}` : ""}
        </span>
      );
    },
  },
  params?.isQuarry
    ? {
        title: "DESCRIPTION",
        key: "description",
        align: "center",
        dataIndex: "description",
        render: (val) => <DescriptionCell text={val} />,
      }
    : params?.isJob
      ? {
          title: "NOTES",
          key: "notes",
          align: "center",
          dataIndex: "notes",
          render: (val) => <DescriptionCell text={val} />,
        }
      : {},
  {
    title: "UPLOAD DATE",
    key: "uploadDate",
    align: "center",
    dataIndex: "date",
    render: (val) => <span className="table-font-mobile">{val}</span>,
  },
  {
    title: "",
    key: "actions",
    dataIndex: "actions",
    fixed: "right",
    align: "right",
    render: (_, record) => {
      return (
        <ConditionalRender visible={!record?.locked && !!!params?.blockedJob}>
          <Popover
            placement={PlacementPositions.LEFT}
            content={
              <>
                <ConditionalRender
                  visible={!record?.docType?.includes("Deprecated")}
                >
                  <div
                    className={styles.PopoverContent}
                    onClick={() => params?.handleEditNavigate(record?.id)}
                  >
                    <MdModeEdit />
                    Edit
                  </div>
                </ConditionalRender>
                <div
                  className={styles.PopoverContent}
                  onClick={() => params?.handleDeleteModalOpen(record?.id)}
                >
                  <MdDelete />
                  Delete
                </div>
              </>
            }
          >
            <EllipsisOutlined
              style={{ padding: "0.5rem", fontSize: "30px", color: "#C3C8CD" }}
              className="action-ellipsis-button"
            />
          </Popover>
        </ConditionalRender>
      );
    },
  },
];

export const DocumentsTableConstants = {
  BUTTON: {
    ADD_DOCUMENT_LABEL: "+ Add Documents",
  },
  MODAL: {
    ADD_DOCUMENT_MODAL_TITLE: "Add Document",
    SUBMIT_BUTTON_LABEL: "Submit",
    WIDTH: "716px",
    DELETE_DOCUMENT_MODAL_TITLE: "Delete Document",
    DELETE_MODAL_CONTENT:
      "This document will be permanently deleted. Are you sure you want to proceed?",
    CONFIRM_BUTTON_LABEL: "Confirm",
    EDIT_DOCUMENT_MODAL_TITLE: "Edit Document",
  },
  MESSAGES: {
    ADD_DOCUMENT_SUCCESS: "Document added successfully!",
    ADD_DOCUMENT_ERROR: "Failed to add document!",
    NO_DOCUMENT_ERROR: "Atleast one document should be uploaded!",
    DELETE_DOCUMENT_SUCCESS: "Document deleted successfully!",
    DELETE_DOCUMENT_ERROR: "Failed to delete document!",
    EDIT_DOCUMENT_SUCCESS: "Document updated successfully!",
    EDIT_DOCUMENT_FAILURE: "Failed to update the document!",
  },
};

export const documentsValidationSchema = (isQuarry?: boolean) => {
  return Yup.object().shape({
    docName: Yup.string().required("Document name is required!"),
    date: Yup.string().required("Date is required!"),
    docType: Yup.string()
      .oneOf(Object.values(DocumentTypes))
      .when([], {
        is: () => !!!isQuarry,
        then: (schema) => schema.required("Document type is required!"),
        else: (schema) => schema.notRequired(),
      }),
    materialId: Yup.string().notRequired(),
  });
};

export const documentsDeletedTableMap = {
  [DatabaseSections.QUARRY]: ArchiveKeys.DOCUMENTS,
  [DatabaseSections.JOBS]: ArchiveKeys.DOCUMENTS,
  [DatabaseSections.CUSTOMER]: ArchiveKeys.DOCUMENTS,
};

export const documentsIdMap = {
  [DatabaseSections.QUARRY]: ArchiveId.QUARRY_ID,
};
